/**
 * API client configuration with Axios.
 *
 * Features:
 * - Base URL from environment (VITE_API_BASE_URL) or default '/api'
 * - Auth token interceptor from Keycloak adapter
 * - X-Request-ID header generation for tracing
 * - 401 response handling (trigger re-auth)
 * - 429 rate limiting with exponential backoff
 * - Network error handling
 * - 30 second timeout
 */

import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { getToken, refreshToken, isAuthenticated } from '@/lib/keycloak';
import { addRateLimitInterceptor } from './rateLimitInterceptor';
import { dispatchSessionExpiredEvent } from '@/hooks/useSessionExpiration';
import { USER_LOCALE_HEADER, getUserLocale } from '@/lib/userLocale';
import { ORG_ID_HEADER, orgIdForApiUrl } from '@/features/orgs/orgHeader';

/**
 * Generate a UUID v4 for request tracing
 */
function generateRequestId(): string {
  return crypto.randomUUID();
}

/**
 * Create and configure the Axios instance
 */
function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
    timeout: 30000, // 30 seconds
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor: Add auth token and request ID
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // Add X-Request-ID header for distributed tracing
      config.headers.set('X-Request-ID', generateRequestId());

      // Always send the user's IANA timezone so server-side crews
      // (e.g. meeting-scheduler) encode times correctly in the
      // user's local zone instead of silently defaulting to
      // America/New_York. User-prefs ``timezone`` is the canonical
      // source server-side; this header is the fallback when the
      // user hasn't set a preference.
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) {
          config.headers.set('X-User-Timezone', tz);
        }
      } catch {
        // No Intl support — server falls back to user-prefs/default.
      }

      // Send the UI language ("en" or "es") so text the server writes on the
      // user's behalf once (the What's new post in the main thread) is in the
      // language the user reads. The server falls back to English without it.
      config.headers.set(USER_LOCALE_HEADER, getUserLocale());

      // Andi for Business: X-Org-ID only inside a business workspace and only for the
      // chats routes (see features/orgs/orgHeader). Personal requests are unchanged.
      const orgId = orgIdForApiUrl(config.url);
      if (orgId) {
        config.headers.set(ORG_ID_HEADER, orgId);
      }

      // Get auth token from Keycloak adapter. If the current token slot is
      // empty but the user is still authenticated (Keycloak instance just
      // hasn't refreshed yet), try a refresh before giving up — otherwise
      // we ship an un-auth'd request and the backend 401s before the route
      // handler runs. This recently bit the Plaid /v1/plaid/exchange call:
      // by the time onSuccess fired from the SDK, getToken() was undefined,
      // the original `if (token)` guard skipped refresh entirely, and the
      // backend logged "No authentication token in request".
      let token = getToken();
      const authed = isAuthenticated();

      if (!token && authed) {
        try {
          const refreshed = await refreshToken(30);
          if (refreshed) {
            token = getToken();
          } else {
            // updateToken returned false (token still valid in Keycloak's
            // view) — re-read the slot in case it was repopulated.
            token = getToken();
          }
        } catch (error) {
          console.warn('Token refresh on empty slot failed:', error);
        }
      } else if (token) {
        try {
          const refreshed = await refreshToken(30);
          if (refreshed) {
            token = getToken();
          }
        } catch (error) {
          console.warn('Token refresh failed during request:', error);
        }
      }

      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
      } else if (authed) {
        // Authenticated but still no token after refresh attempt — surface
        // this so it's grep-able next time something like the Plaid flow
        // misbehaves.
        console.warn(
          'apiClient: dispatching request without Authorization header despite isAuthenticated()=true',
          { url: config.url, method: config.method }
        );
      }

      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor: Handle errors
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error: AxiosError) => {
      // Handle 401 Unauthorized - attempt silent refresh, then trigger re-auth modal
      if (error.response?.status === 401) {
        console.warn('Received 401 Unauthorized, attempting silent refresh');

        // Attempt silent token refresh
        try {
          const refreshed = await refreshToken(30);
          if (refreshed && isAuthenticated()) {
            // Retry the original request with the new token
            const originalRequest = error.config;
            if (originalRequest) {
              const newToken = getToken();
              if (newToken) {
                originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
              }
              return await client.request(originalRequest);
            }
          }
        } catch (refreshError) {
          console.warn('Silent token refresh failed:', refreshError);
        }

        // If refresh failed, dispatch session expired event
        // This will trigger the re-auth modal instead of immediate redirect
        dispatchSessionExpiredEvent();
        return Promise.reject(error);
      }

      // Handle network errors
      if (!error.response) {
        console.error('Network error:', error.message);
        // Enhance error message for network issues
        const networkError = new Error(
          'Network error: Unable to connect to the server. Please check your connection.'
        );
        (networkError as Error & { originalError: AxiosError }).originalError = error;
        return Promise.reject(networkError);
      }

      // Handle timeout
      if (error.code === 'ECONNABORTED') {
        console.error('Request timeout:', error.message);
        const timeoutError = new Error('Request timed out. Please try again.');
        (timeoutError as Error & { originalError: AxiosError }).originalError = error;
        return Promise.reject(timeoutError);
      }

      // Surface the backend's `detail` message (FastAPI's standard
      // HTTPException/validation error shape) instead of axios's generic
      // "Request failed with status code NNN". Without this, every UI that
      // renders error.message directly (e.g. schedule-preview validation
      // errors) leaks a raw HTTP status code to the user instead of the
      // specific, actionable message the backend already computed.
      //
      // Mutates error.message on the ORIGINAL AxiosError (rather than
      // rejecting with a new plain Error) so isAxiosError(error) and
      // error.response?.status keep working for every existing call site
      // that branches on status code (e.g. useRunTaskNow's 409 "already
      // running" check) — only the human-readable text changes.
      const data: unknown = error.response.data;
      if (data && typeof data === 'object' && 'detail' in data) {
        const detail = (data as { detail?: unknown }).detail;
        let message: string | undefined;
        if (typeof detail === 'string') {
          message = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation error array: [{ loc, msg, type }, ...].
          // Joined with '; ' rather than a bare space so multiple field
          // errors read as a list instead of running together into one
          // confusing sentence (e.g. "field required value is not a
          // valid email address" -> "field required; value is not a
          // valid email address").
          message = detail
            .map((d) =>
              d && typeof d === 'object' && 'msg' in d ? String((d as { msg?: unknown }).msg) : null
            )
            .filter((m): m is string => !!m)
            .join('; ');
        }
        if (message) {
          error.message = message;
          (error as AxiosError & { status?: number }).status = error.response.status;
        }
      }

      return Promise.reject(error);
    }
  );

  // Add rate limiting interceptor with exponential backoff
  addRateLimitInterceptor(client, {
    maxRetries: 3,
    baseDelay: 1000,
    maxDelay: 60000,
    showToasts: true,
  });

  return client;
}

/**
 * Pre-configured Axios instance for API requests.
 *
 * Usage:
 * ```typescript
 * import { apiClient } from '@/api/client';
 *
 * // GET request
 * const response = await apiClient.get('/v1/chats');
 *
 * // POST request
 * const response = await apiClient.post('/v1/chats', { title: 'New Chat' });
 *
 * // With custom headers
 * const response = await apiClient.get('/v1/files', {
 *   headers: { 'X-Custom-Header': 'value' }
 * });
 * ```
 */
export const apiClient = createApiClient();

/**
 * Type-safe wrapper for GET requests
 */
export async function get<T>(
  url: string,
  config?: Parameters<typeof apiClient.get>[1]
): Promise<T> {
  const response = await apiClient.get<T>(url, config);
  return response.data;
}

/**
 * Type-safe wrapper for POST requests
 */
export async function post<T>(
  url: string,
  data?: unknown,
  config?: Parameters<typeof apiClient.post>[2]
): Promise<T> {
  const response = await apiClient.post<T>(url, data, config);
  return response.data;
}

/**
 * Type-safe wrapper for PUT requests
 */
export async function put<T>(
  url: string,
  data?: unknown,
  config?: Parameters<typeof apiClient.put>[2]
): Promise<T> {
  const response = await apiClient.put<T>(url, data, config);
  return response.data;
}

/**
 * Type-safe wrapper for PATCH requests
 */
export async function patch<T>(
  url: string,
  data?: unknown,
  config?: Parameters<typeof apiClient.patch>[2]
): Promise<T> {
  const response = await apiClient.patch<T>(url, data, config);
  return response.data;
}

/**
 * Type-safe wrapper for DELETE requests
 */
export async function del<T>(
  url: string,
  config?: Parameters<typeof apiClient.delete>[1]
): Promise<T> {
  const response = await apiClient.delete<T>(url, config);
  return response.data;
}

export default apiClient;
